"use client";

import React, { useState, useEffect } from 'react';
import {
  X,
  Bell,
  TrendingUp,
  TrendingDown,
  Calendar,
  CreditCard,
  AlertTriangle,
  BarChart3,
  Clock,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Info,
} from 'lucide-react';
import type {
  SpendingAlert,
  AnomalyMetadata,
  BillReminderMetadata,
  SubscriptionChangeMetadata,
  LargeTransactionMetadata,
  BudgetAlertMetadata,
} from './types';

interface AlertDetailViewProps {
  alert: SpendingAlert;
  onClose: () => void;
  onDismiss?: (alertId: string) => void;
  onSnooze?: (alertId: string, until: Date) => void;
  onFeedback?: (alertId: string, feedback: 'useful' | 'not_useful') => void;
  onViewTransactions?: () => void;
  onMarkAsRead?: () => void;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatShortDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const AlertDetailView: React.FC<AlertDetailViewProps> = ({
  alert,
  onClose,
  onDismiss,
  onSnooze,
  onFeedback,
  onViewTransactions,
  onMarkAsRead,
}) => {
  const [showSnoozePicker, setShowSnoozePicker] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<'useful' | 'not_useful' | null>(
    alert.userFeedback || null
  );

  useEffect(() => {
    if (onMarkAsRead) {
      onMarkAsRead();
    }
  }, [onMarkAsRead]);

  const handleFeedback = (feedback: 'useful' | 'not_useful') => {
    setSelectedFeedback(feedback);
    if (onFeedback) {
      onFeedback(alert.id, feedback);
    }
  };

  const snoozeOptions = [
    { label: '1 hour', hours: 1 },
    { label: 'Until tomorrow', hours: 24 },
    { label: 'Until next week', hours: 168 },
  ];

  const renderMetadataDetails = () => {
    switch (alert.category) {
      case 'anomaly': {
        const meta = alert.metadata as AnomalyMetadata;
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your Spending</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(meta.currentAmount)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Usual Amount</p>
                <p className="text-xl font-bold text-gray-400">{formatCurrency(meta.baselineAmount)}</p>
              </div>
            </div>

            <div className="bg-accent-warning/10 border border-accent-warning/20 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-accent-warning" />
                <span className="text-sm font-semibold text-gray-900">
                  {meta.multiplier.toFixed(1)}x your normal
                </span>
              </div>
              <p className="text-sm text-gray-600">
                You've spent{' '}
                <span className="font-semibold text-gray-900">
                  {formatCurrency(meta.currentAmount - meta.baselineAmount)}
                </span>{' '}
                more than usual on{' '}
                <span className="font-semibold text-gray-900">{meta.category}</span> this period.
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <BarChart3 className="h-4 w-4" />
              <span>Based on {meta.transactionCount} transactions in this category</span>
            </div>
            </div>
          );
      }

      case 'bill_reminder': {
        const meta = alert.metadata as BillReminderMetadata;
        const daysUntilDue = Math.ceil(
          // eslint-disable-next-line
          (new Date(meta.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );

        return (
          <div className="space-y-4">
            <div className="bg-accent-authority/10 border border-accent-authority/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-accent-authority" />
                  <span className="font-semibold text-gray-900">{meta.merchantName}</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{formatCurrency(meta.amount)}</span>
              </div>
              <p className="text-sm text-gray-600">
                {daysUntilDue === 0 ? (
                  <span className="text-accent-warning font-medium">Due today</span>
                ) : daysUntilDue === 1 ? (
                  <span className="text-accent-warning font-medium">Due tomorrow</span>
                ) : daysUntilDue > 0 ? (
                  <>
                    Due in <span className="font-medium">{daysUntilDue} days</span>
                  </>
                ) : (
                  <span className="text-accent-negative font-medium">Overdue</span>
                )}
                {' - '}
                {formatShortDate(meta.dueDate)}
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>{meta.recurringPattern}</span>
            </div>
          </div>
        );
      }

      case 'subscription_change': {
        const meta = alert.metadata as SubscriptionChangeMetadata;
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-gray-900">{meta.merchantName}</span>
                <span className={`text-lg font-bold ${meta.isIncrease ? 'text-accent-negative' : 'text-accent-positive'}`}>
                  {meta.isIncrease ? '+' : '-'}{formatCurrency(Math.abs(meta.newAmount - meta.oldAmount))}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-0.5">Was</p>
                  <p className="text-sm text-gray-400 line-through">{formatCurrency(meta.oldAmount)}</p>
                </div>
                <div className="flex items-center justify-center w-8">
                  <TrendingUp className={`h-4 w-4 ${meta.isIncrease ? 'text-accent-negative rotate-0' : 'text-accent-positive rotate-180'}`} />
                </div>
                <div className="flex-1 text-right">
                  <p className="text-xs text-gray-500 mb-0.5">Now</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(meta.newAmount)}</p>
                </div>
              </div>
            </div>

            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
              meta.isIncrease
                ? 'bg-accent-negative/10 text-accent-negative'
                : 'bg-accent-positive/10 text-accent-positive'
            }`}>
              {meta.isIncrease ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              <span>{meta.changePercentage.toFixed(0)}% {meta.isIncrease ? 'increase' : 'decrease'}</span>
            </div>
          </div>
        );
      }

      case 'large_transaction': {
        const meta = alert.metadata as LargeTransactionMetadata;
        return (
          <div className="space-y-4">
            <div className="bg-accent-authority/10 border border-accent-authority/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-accent-authority" />
                  <span className="font-semibold text-gray-900">{meta.merchantName}</span>
                </div>
                <span className="text-xl font-bold text-gray-900">{formatCurrency(meta.amount)}</span>
              </div>
              <p className="text-sm text-gray-600">
                Transaction on {formatShortDate(meta.transactionDate)}
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <AlertTriangle className="h-4 w-4" />
              <span>
                This is {formatCurrency(meta.amount - meta.threshold)} above your threshold of{' '}
                {formatCurrency(meta.threshold)}
              </span>
            </div>
          </div>
        );
      }

      case 'budget_alert': {
        const meta = alert.metadata as BudgetAlertMetadata;
        const progressPercent = Math.min(meta.percentageUsed, 100);

        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-gray-900">{meta.category}</span>
              <span className="text-sm text-gray-500">
                {meta.daysRemaining} days remaining
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {formatCurrency(meta.spent)} of {formatCurrency(meta.budget)}
                </span>
                <span className={`font-semibold ${meta.percentageUsed > 100 ? 'text-accent-negative' : 'text-gray-900'}`}>
                  {meta.percentageUsed.toFixed(0)}%
                </span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    meta.percentageUsed > 100
                      ? 'bg-accent-negative'
                      : meta.percentageUsed > 80
                      ? 'bg-accent-warning'
                      : 'bg-accent-positive'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {meta.percentageUsed > 100 && (
              <div className="bg-accent-negative/10 border border-accent-negative/20 rounded-lg p-3">
                <p className="text-sm text-accent-negative font-medium">
                  You've exceeded this budget by {formatCurrency(meta.spent - meta.budget)}
                </p>
              </div>
            )}
          </div>
        );
      }

      case 'weekly_summary':
        return (
          <div className="text-center py-4">
            <BarChart3 className="h-8 w-8 text-accent-positive mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              View your full weekly summary to see detailed breakdown of spending across all categories.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col animate-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              alert.severity === 'high' ? 'bg-accent-negative/10' :
              alert.severity === 'medium' ? 'bg-accent-warning/10' : 'bg-gray-100'
            }`}>
              <Bell className={`h-5 w-5 ${
                alert.severity === 'high' ? 'text-accent-negative' :
                alert.severity === 'medium' ? 'text-accent-warning' : 'text-gray-500'
              }`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Alert Details</h2>
              <p className="text-xs text-gray-500">{formatDate(alert.triggeredAt)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{alert.title}</h3>
            <p className="text-gray-600">{alert.message}</p>
          </div>

          {renderMetadataDetails()}

          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Was this alert useful?</h4>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleFeedback('useful')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors cursor-pointer ${
                  selectedFeedback === 'useful'
                    ? 'bg-accent-positive text-white border-accent-positive'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-accent-positive'
                }`}
              >
                <ThumbsUp className="h-4 w-4" />
                <span className="text-sm font-medium">Yes, useful</span>
              </button>
              <button
                onClick={() => handleFeedback('not_useful')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors cursor-pointer ${
                  selectedFeedback === 'not_useful'
                    ? 'bg-accent-negative text-white border-accent-negative'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-accent-negative'
                }`}
              >
                <ThumbsDown className="h-4 w-4" />
                <span className="text-sm font-medium">Not really</span>
              </button>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-accent-authority flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">Information only</p>
                <p className="text-xs text-gray-600">
                  This alert is for informational purposes only and does not constitute financial advice.
                  Please consider your personal circumstances before making any financial decisions.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {onViewTransactions && (
              <button
                onClick={onViewTransactions}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-authority text-white rounded-lg hover:bg-accent-authority/90 transition-colors font-medium text-sm cursor-pointer"
              >
                View Transactions
                <ExternalLink className="h-4 w-4" />
              </button>
            )}

            <div className="relative">
              <button
                onClick={() => setShowSnoozePicker(!showSnoozePicker)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm cursor-pointer"
              >
                <Clock className="h-4 w-4" />
                Snooze
              </button>

              {showSnoozePicker && (
                <div className="absolute bottom-full mb-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[160px]">
                  {snoozeOptions.map((option) => (
                    <button
                      key={option.hours}
                      onClick={() => {
                        if (!onSnooze) return;
                        const until = new Date();
                        until.setHours(until.getHours() + option.hours);
                        onSnooze(alert.id, until);
                        setShowSnoozePicker(false);
                      }}
                      className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left transition-colors cursor-pointer"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => onDismiss?.(alert.id)}
              className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm cursor-pointer"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertDetailView;